<template>
    <div
        class="group my-4 rounded-lg border-iron-200 bg-white px-4 py-5 shadow-sm hover:shadow-md w-full border transition-all duration-200"
    >
        <div class="space-x-3 sm:flex items-center justify-between">
            <div class="gap-2 flex items-center">
                <div class="flex-shrink-0"></div>
                <div class="min-w-0 gap-2 flex flex-wrap items-center">
                    <p class="font-medium text-iron-900">
                        {{ pageDomain.domain }}
                    </p>

                    <Badge v-if="pageDomain.is_default" variant="secondary">
                        {{ $t("page_domains.default") }}
                    </Badge>

                    <Badge v-if="pageDomain.verified_at" variant="success">
                        {{ $t("page_domains.verified") }}
                    </Badge>
                    <Badge v-else variant="destructive">
                        {{ $t("page_domains.not_verified") }}
                    </Badge>
                </div>
            </div>

            <div
                class="mt-3 sm:mt-0 flex flex-shrink-0 justify-start self-center"
            >
                <div class="gap-2 flex">
                    <ManagePageDomain
                        :page-domain-id="pageDomain.id"
                        :page-domain-domain="pageDomain.domain"
                        :page-domain-is-default="pageDomain.is_default"
                    >
                        <Button size="sm" variant="outline" class="gap-1">
                            <Edit2 :size="16" :stroke-width="1.5" />
                            <span class="sm:inline-block hidden">{{
                                $t("edit")
                            }}</span>
                        </Button>
                    </ManagePageDomain>

                    <Button
                        v-if="!pageDomain.is_default"
                        size="sm"
                        variant="outline"
                        class="gap-1"
                        :disabled="loading"
                        @click="handleSetDefault"
                    >
                        <Star :size="16" :stroke-width="1.5" />
                        <span class="sm:inline-block hidden">{{
                            $t("page_domains.set_default")
                        }}</span>
                    </Button>

                    <PageDomainDnsModal
                        :page-domain-id="pageDomain.id"
                        :domain="pageDomain.domain"
                        :verified-at="pageDomain.verified_at"
                        :cf-status="pageDomain.cf_status"
                        :cf-ssl="pageDomain.cf_ssl"
                        :cf-ownership-verification="
                            pageDomain.cf_ownership_verification
                        "
                        :dns-validation="pageDomain.dns_validation"
                    >
                        <Button size="sm" variant="outline" class="gap-1">
                            <Settings :size="16" :stroke-width="1.5" />
                            <span class="sm:inline-block hidden">{{
                                $t("page_domains.dns_setup.button")
                            }}</span>
                        </Button>
                    </PageDomainDnsModal>

                    <Button
                        v-if="!pageDomain.verified_at"
                        size="sm"
                        variant="outline"
                        class="gap-1"
                        :disabled="loading"
                        @click="handleVerify"
                    >
                        <RefreshCw :size="16" :stroke-width="1.5" />
                        <span class="sm:inline-block hidden">{{
                            $t("page_domains.verify")
                        }}</span>
                    </Button>

                    <Button
                        size="icon"
                        class="h-9 w-9"
                        variant="outline"
                        :disabled="loading"
                        @click="handleDelete"
                    >
                        <Trash2 :size="16" :stroke-width="1.5" />
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import ManagePageDomain from "./ManagePageDomain.vue";
import PageDomainDnsModal from "./PageDomainDnsModal.vue";
import useConfirm from "../../ConfirmDialog/composables/useConfirm";
import { useI18n } from "vue-i18n";
import { Trash2, Edit2, Star, RefreshCw, Settings } from "lucide-vue-next";
import { ref } from "vue";
import type { PageDomainDTO } from "@/types/PageDomains";
import { usePageDomains } from "@/composables/usePageDomains";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const { show } = useConfirm();
const { t } = useI18n();
const { deletePageDomain, setDefaultPageDomain, verifyPageDomain } =
    usePageDomains();

const props = defineProps<{
    pageDomain: PageDomainDTO;
}>();

const loading = ref(false);

const handleDelete = async () => {
    const confirmed = await show({
        header: t("page_domains.confirm_delete.title"),
        message: t("page_domains.confirm_delete.message"),
        confirm: t("page_domains.confirm_delete.confirm"),
        cancel: t("page_domains.confirm_delete.cancel"),
    });

    if (!confirmed) {
        return;
    }

    deletePageDomain(props.pageDomain.id);
};

const handleSetDefault = () => {
    loading.value = true;
    setDefaultPageDomain(props.pageDomain.id, {
        onFinish: () => {
            loading.value = false;
        },
    });
};

const handleVerify = () => {
    loading.value = true;
    verifyPageDomain(props.pageDomain.id, {
        onFinish: () => {
            loading.value = false;
        },
    });
};
</script>
